import { formatToHumanReadable } from "@/lib/format-data";

/**
 * How a select option reads once a measurement field has given it a label.
 *
 * The option itself stays the submitted value — a bound question stores the number,
 * conditions compare against it, and a derived field sums it. The label is only ever
 * shown, so every renderer composes it here rather than deciding on its own.
 *
 * Both halves show: for a scored instrument the number is what carries into the total,
 * and the words are what make it answerable.
 */
export const optionDisplay = (option: string, label?: string): string => {
  const trimmed = label?.trim();
  return trimmed ? `${option} - ${trimmed}` : option;
};

/**
 * The parallel `options` / `optionLabels` arrays as a lookup by submitted value.
 *
 * Answer readback knows the value a patient sent, not its position, and some surfaces
 * (the patient portal) render the answer without the option list it came from.
 *
 * The map has no prototype: an option value is whatever the clinic typed, and on an
 * ordinary object a value of `toString` or `constructor` reads back as the inherited
 * function rather than as a missing label.
 */
export const optionLabelsByValue = (
  options?: string[],
  optionLabels?: string[],
): Record<string, string> | undefined => {
  if (!options || !optionLabels) return undefined;
  const byValue: Record<string, string> = Object.create(null);
  options.forEach((option, index) => {
    const label = optionLabels[index];
    if (label) byValue[option] = label;
  });
  return byValue;
};

/**
 * How a stored option value reads on its own, before any label is appended.
 *
 * Options are typed by the clinic, so their casing is a deliberate choice — "PRP",
 * "IPL photofacial", "preOp photos" all mean something the way they were written, and
 * title-casing them reads as a bug. Only a machine-shaped value has no authored casing
 * to respect: an imported enum such as `NO_ADVERSE_REACTION`, which is upper snake case
 * and nothing a clinic types into an option box.
 *
 * The lowercase test is what keeps that narrow. An option authored `B12_injection` or
 * `IPL_photofacial` holds an underscore and is still the clinic's wording, so the
 * underscore alone would re-case exactly the values this exists to leave alone.
 */
export const formatOptionValue = (option: string): string =>
  option && option.includes("_") && option === option.toUpperCase()
    ? formatToHumanReadable(option)
    : (option ?? "");

/**
 * The formatter for a question's options while it is being filled in.
 *
 * A gender-mapped question stores the patient enum (`MALE`, `FEMALE`) as its options, so
 * those read humanized; every other question shows its options as the clinic typed them.
 */
export const questionOptionFormatter = (
  mappingKey?: string,
): ((option: string) => string) =>
  mappingKey === "gender" ? formatToHumanReadable : formatOptionValue;
